import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero, Eyebrow } from "../components/Section";
import { BRAND } from "../lib/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  organisation: "",
  email: "",
  phone: "",
  message: "",
  inquiry_type: "general",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Required";
    if (!form.email.trim()) er.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Invalid email";
    if (!form.message.trim()) er.message = "Required";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Inquiry received. We will respond shortly.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        num="01"
        title={<>Start a <span className="italic text-[var(--bjc-gold)]">conversation</span>.</>}
        intro="For advisory engagements, partnerships, or speaking enquiries — share a brief outline below. Senior responses, typically within one business day."
      />

      <section className="bjc-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <aside className="md:col-span-4 space-y-10">
            <div>
              <Eyebrow num="02">Direct</Eyebrow>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-direct-email"
                className="mt-5 block font-serif-display text-2xl md:text-3xl text-[var(--bjc-text)] hover:text-[var(--bjc-gold)] transition-colors break-all"
              >
                {BRAND.email}
              </a>
            </div>

            <div>
              <div className="bjc-overline">Engagement Types</div>
              <ul className="mt-5 space-y-3 text-[var(--bjc-text-muted)] text-sm">
                <li>· Advisory & independent review</li>
                <li>· Regulatory defence & remediation</li>
                <li>· Speaking engagements</li>
                <li>· Strategic partnerships</li>
              </ul>
            </div>

            <div>
              <div className="bjc-overline">Coverage</div>
              <p className="mt-5 text-[var(--bjc-text-muted)] text-sm leading-relaxed">
                India · Middle East · Asia-Pacific. Cross-border engagements considered on a
                case-by-case basis.
              </p>
            </div>
          </aside>

          <div className="md:col-span-7 md:col-start-6">
            {submitted ? (
              <div data-testid="contact-success" className="bjc-card p-10 md:p-14">
                <div className="w-12 h-12 inline-flex items-center justify-center border border-[var(--bjc-gold)] text-[var(--bjc-gold)]">
                  <Check size={20} />
                </div>
                <h2 className="mt-8 font-serif-display text-3xl md:text-4xl text-[var(--bjc-text)] leading-[1.1]">
                  Inquiry received.
                </h2>
                <p className="mt-4 text-[var(--bjc-text-muted)] leading-relaxed">
                  Thank you for reaching out to Bharat Jeswani Consulting. A senior member of the
                  practice will respond directly, typically within one business day.
                </p>
                <button
                  type="button"
                  data-testid="contact-submit-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-10 bjc-link"
                >
                  Submit Another Inquiry <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                noValidate
                className="bjc-card p-8 md:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label="Name" required>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      data-testid="contact-input-name"
                      placeholder="Your full name"
                      className="bjc-input"
                    />
                    {errors.name && <FieldError>{errors.name}</FieldError>}
                  </Field>

                  <Field label="Organisation">
                    <input
                      type="text"
                      name="organisation"
                      value={form.organisation}
                      onChange={onChange}
                      data-testid="contact-input-organisation"
                      placeholder="Bank, fintech, law firm, etc."
                      className="bjc-input"
                    />
                  </Field>

                  <Field label="Email" required>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      placeholder="name@institution.com"
                      className="bjc-input"
                    />
                    {errors.email && <FieldError>{errors.email}</FieldError>}
                  </Field>

                  <Field label="Phone">
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="contact-input-phone"
                      placeholder="+91 / +971 / +65 …"
                      className="bjc-input"
                    />
                  </Field>
                </div>

                <Field label="Inquiry Type">
                  <select
                    name="inquiry_type"
                    value={form.inquiry_type}
                    onChange={onChange}
                    data-testid="contact-input-type"
                    className="bjc-input"
                  >
                    <option value="general">General Advisory</option>
                    <option value="audit">AML Audit & Independent Review</option>
                    <option value="compliance">AML Compliance & Program Design</option>
                    <option value="technology">Financial Crime Technology</option>
                    <option value="defence">Regulatory Defence & Remediation</option>
                    <option value="speaking">Speaking & Training</option>
                    <option value="partner">Partnership</option>
                  </select>
                </Field>

                <Field label="Message" required>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    placeholder="Outline the context, objective, and any relevant background. We treat all inquiries with discretion."
                    className="bjc-input"
                    rows={5}
                  />
                  {errors.message && <FieldError>{errors.message}</FieldError>}
                </Field>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                  <p className="text-xs text-[var(--bjc-text-faint)] tracking-wide">
                    Submission is confidential and used only to respond to this inquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="bjc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        Submit Inquiry <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block bjc-overline mb-3">
        {label}
        {required && <span className="text-[var(--bjc-gold)]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function FieldError({ children }) {
  return <span className="block mt-2 text-[11px] tracking-wider uppercase text-red-400">{children}</span>;
}
